import React from "react";
import { Box } from "braid-design-system";
import Loading from "../components/Loading";
import CustomerListTable from "../components/Customer/CustomerListTable";
import CreateUpdateCustomerDrawer, {
    CustomerFormData,
    CustomerToEdit,
} from "../components/Customer/CreateUpdateCustomerDrawer";
import {
    createCustomer,
    CustomerCreateUpdateRequest,
    CustomerResponse,
    getAllCustomers,
    updateCustomer,
} from "../services/customerService";
import { useToast } from "../hooks/useToast";
import { useCanManageMasters } from "../hooks/useCanManageMasters";

const toRequestPayload = (formData: CustomerFormData): CustomerCreateUpdateRequest => ({
    cus_accno: formData.cus_accno,
    cus_code: formData.cus_code,
    cus_name: formData.cus_name,
    cus_title: formData.cus_title,
    cus_groupid: formData.cus_groupid,
    cus_active: formData.cus_active,
    cus_add1: formData.cus_add1,
    cus_add2: formData.cus_add2,
    cus_add3: formData.cus_add3,
    cus_add4: formData.cus_add4,
    cus_tep1: formData.cus_tep1,
    cus_tep2: formData.cus_tep2,
    cus_email: formData.cus_email,
    cus_dob: formData.cus_dob || null,
    cus_prof: formData.cus_prof,
    cus_spouse: formData.cus_spouse,
    cus_creditlimit: formData.cus_creditlimit,
    cus_creditperiod: formData.cus_creditperiod,
    cus_opbal: formData.cus_opbal,
    cus_purchase: formData.cus_purchase,
    cus_returns: formData.cus_returns,
    cus_creditnote: formData.cus_creditnote,
    cus_debitnote: formData.cus_debitnote,
    cus_payment: formData.cus_payment,
    cus_balance: formData.cus_balance,
    cus_loyaltyactive: formData.cus_loyaltyactive,
    cus_openpoint: formData.cus_openpoint,
    cus_earnepoint: formData.cus_earnepoint,
    cus_redeempoint: formData.cus_redeempoint,
    cus_points: formData.cus_points,
});

const CustomerManagement: React.FC = () => {
    const toast = useToast();
    const canManageMasters = useCanManageMasters();
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState<string | null>(null);
    const [customerList, setCustomerList] = React.useState<CustomerResponse[]>([]);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [selectedCustomer, setSelectedCustomer] = React.useState<CustomerToEdit | undefined>(undefined);

    const fetchCustomers = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingMessage("Please wait, Fetching customers...");
            const data = await getAllCustomers();
            setCustomerList(data);
        } catch (error: any) {
            toast.showError(`Error fetching customers: ${error?.message || "Unknown error"}`);
            setCustomerList([]);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    }, [toast]);

    React.useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    const handleSaveCustomer = React.useCallback(
        async (customerData: CustomerFormData, editingCustomer?: CustomerToEdit) => {
            if (editingCustomer?.cus_id) {
                const payload = {
                    ...toRequestPayload(customerData),
                    cus_opbal: editingCustomer.cus_opbal ?? null,
                    cus_balance: editingCustomer.cus_balance ?? null,
                };
                await updateCustomer(editingCustomer.cus_id, payload);
            } else {
                const payload = toRequestPayload(customerData);
                await createCustomer(payload);
            }
        },
        [],
    );

    const handleEditCustomer = React.useCallback((customer: CustomerResponse) => {
        setSelectedCustomer({
            ...customer,
            cus_id: customer.cus_id ?? undefined,
            cus_dob: customer.cus_dob || "",
            cus_active: Boolean(customer.cus_active),
            cus_loyaltyactive: Boolean(customer.cus_loyaltyactive),
        });
        setDrawerOpen(true);
    }, []);

    const handleCloseDrawer = React.useCallback(() => {
        setDrawerOpen(false);
        setSelectedCustomer(undefined);
    }, []);

    return (
        <>
            {loading && <Loading message={loadingMessage || "Loading..."} />}

            <Box padding="medium">
                <CustomerListTable
                    customerList={customerList}
                    onCreateCustomer={canManageMasters ? () => setDrawerOpen(true) : undefined}
                    onEditCustomer={handleEditCustomer}
                />
            </Box>

            <CreateUpdateCustomerDrawer
                isOpen={drawerOpen}
                onClose={handleCloseDrawer}
                customerToEdit={selectedCustomer}
                onSave={handleSaveCustomer}
                onCustomerSaved={fetchCustomers}
                isReadOnly={!canManageMasters}
            />
        </>
    );
};

export default CustomerManagement;
